/**
 * Export/import **FIDO Credential Exchange Format** (CXF v1.0, 09/03/2026) —
 * item R8. Le CXF est le format d'echange que Apple Passwords, Google Password
 * Manager, 1Password, Bitwarden et Dashlane savent produire et relire : c'est
 * a la fois la porte d'entree (recuperer un utilisateur qui vient d'ailleurs)
 * et la porte de sortie (aucun enfermement — on part avec ses donnees).
 *
 * Un CXF transporte des secrets EN CLAIR. La specification prevoit de ne
 * l'echanger que sous enveloppe chiffree : CXP (HPKE, brokere par l'OS) pour
 * le transfert direct d'application a application — Phase 3 mobile. Hors OS,
 * Klavox n'ecrit JAMAIS un CXF nu sur disque : la charge est scellee dans une
 * enveloppe `klavox-cxf-export/v1` (Argon2id + AEAD de `@klavox/crypto-core`,
 * aucune primitive nouvelle), ouverte par un **mot de passe d'export** choisi
 * par l'utilisateur — jamais le mot de passe maitre, jamais conserve, jamais
 * journalise. A la LECTURE, un CXF nu produit par un autre gestionnaire est
 * accepte (c'est ce que l'utilisateur a entre les mains), mais jamais produit.
 *
 * Module pur : zero E/S, zero reseau. L'UI lui passe des entrees deja
 * dechiffrees et recupere du texte a telecharger, ou l'inverse.
 */
import { argon2idAsync } from '@noble/hashes/argon2.js';
import {
  DEFAULT_KDF_PARAMS,
  DecryptionFailedError,
  SYMMETRIC_KEY_LENGTH,
  SecretKey,
  assertKdfParams,
  base64ToBytes,
  bytesToBase64,
  bytesToBase64Url,
  bytesToUtf8,
  openFromString,
  randomBytes,
  sealToString,
  utf8ToBytes,
  STEAM_CODE_LENGTH,
  TOTP_DEFAULTS,
  parseTotpSeed,
  type EntryCustomField,
  type EntrySecrets,
  type TotpSeed,
  type KdfParams,
  type SealedString,
} from '@klavox/crypto-core';
import type { VaultFolder, VaultItem, VaultItemType } from '@klavox/vault-model';
import {
  ImportPasswordInvalidError,
  ImportPasswordRequiredError,
  joinNotes,
  normalizeTotpSecret,
  type ImportedEntry,
} from './csv-import.js';
import { checkboxValue } from './custom-fields.js';
import { isArchived } from './entry-archive.js';
import { readLinks } from './entry-links.js';
import { normalizeTags, readTags } from './entry-tags.js';
import { isRepromptProtected } from './master-password-reprompt.js';
import { IGNORABLE_HEALTH_ALERTS, isHealthExcluded, readIgnoredHealthAlerts, type IgnorableHealthAlert } from './health-exclusion.js';
import { readPasskeys, type PasskeyCredential } from './passkey-credential.js';
import { lastPasswordChange, readPasswordHistory, type PasswordHistoryEntry } from './password-history.js';
import { readPasswordExpiry, readPasswordRenewDays } from './password-expiry.js';
import { readSshKey, type SshKeyMaterial } from './ssh-key-item.js';
import { decodeCxfPrivateKey, pkcs8Pem, sshKeyFromPkcs8, sshKeyToPkcs8, toCxfBase64Url } from './ssh-key-pkcs8.js';
import {
  API_KEY_FIELDS,
  BANK_FIELDS,
  DATABASE_FIELDS,
  EMAIL_FIELDS,
  MEMBERSHIP_FIELDS,
  MEDICAL_FIELDS,
  SERVER_FIELDS,
  SOFTWARE_LICENSE_FIELDS,
  WALLET_FIELDS,
  isSoftwareLicenseDateKey,
  isMembershipDateKey,
  detectCardBrand,
  normalizeDocumentDate,
  normalizeExpiry,
  normalizeTypedFields,
  readTypedFields,
  typedFieldGroup,
  wifiSecurity,
  type TypedFieldGroup,
} from './typed-fields.js';

/** Enveloppe chiffree Klavox autour d'une charge CXF (le CXF nu n'est jamais ecrit). */
export const CXF_EXPORT_FORMAT = 'klavox-cxf-export/v1';

/** `version` de la charge CXF emise (CXF v1.0 : 0). */
export const CXF_VERSION = 0;

/** `exporter` : identite de l'application emettrice, telle que la lira l'importeur. */
export const CXF_EXPORTER = 'klavox.santvia.eu';

/** Nom de format affiche a l'utilisateur quand le fichier demande son mot de passe. */
export const CXF_FORMAT_LABEL = 'FIDO CXF';

/**
 * Etiquettes (`tags`) d'element portant l'etat de l'entree, comme `favorite` :
 * CXF v1.0 n'a ni archive, ni re-demande du mot de passe maitre, ni exclusion de
 * la Sante du coffre. Sans elles, un aller-retour ramenait une entree archivee
 * dans « Tous les elements », une entree sensible s'ouvrait sans mot de passe
 * maitre et une alerte ecartee revenait. Une alerte ignoree une a une porte
 * une etiquette par alerte (`health-ignored-weak`...).
 */
export const CXF_TAG_FAVORITE = 'favorite';
export const CXF_TAG_ARCHIVED = 'archived';
export const CXF_TAG_REPROMPT = 'reprompt';
export const CXF_TAG_HEALTH_EXCLUDED = 'health-excluded';
export const CXF_TAG_HEALTH_IGNORED: Readonly<Record<IgnorableHealthAlert, string>> = {
  weak: 'health-ignored-weak',
  reused: 'health-ignored-reused',
  old: 'health-ignored-old',
  insecureUri: 'health-ignored-insecure-uri',
  missingTotp: 'health-ignored-missing-totp',
};

/**
 * Les etiquettes ci-dessus sont RESERVEES a l'etat : toute autre etiquette de
 * l'element est une etiquette de l'utilisateur (`entry-tags.ts`) et revient
 * telle quelle. Une etiquette d'utilisateur qui porte l'un de ces noms n'est
 * pas exportee -- elle reviendrait en etat (une entree « archived » serait
 * archivee a l'import).
 */
const CXF_STATE_TAGS: ReadonlySet<string> = new Set([
  CXF_TAG_FAVORITE,
  CXF_TAG_ARCHIVED,
  CXF_TAG_REPROMPT,
  CXF_TAG_HEALTH_EXCLUDED,
  ...Object.values(CXF_TAG_HEALTH_IGNORED),
]);

/** Comparaison exacte, comme a l'import : « Archived » reste une etiquette d'utilisateur, « archived » est l'etat. */
function isStateTag(tag: string): boolean {
  return CXF_STATE_TAGS.has(tag);
}

/** Contexte AEAD : un blob d'export ne peut pas etre presente comme une entree de coffre. */
const CXF_SEAL_CONTEXT = 'klavox|zero-knowledge/v1|cxf-export';

const CXF_SALT_LENGTH = 32;

/**
 * Plancher du mot de passe d'export. Plus haut que celui du mot de passe
 * maitre : ce fichier part de l'appareil, il n'a ni verrouillage, ni
 * limitation de tentatives, ni revocation — seule son entropie le protege.
 */
export const MIN_CXF_PASSWORD_LENGTH = 12;

/** Identifiant du champ portant l'historique des mots de passe (round-trip Klavox -> Klavox). */
const PASSWORD_HISTORY_FIELD_ID = 'klavox-password-history';

/**
 * Identifiant du champ portant la date de pose du mot de passe courant : le CXF v1.0 ne date que l'element. Sans lui,
 * Klavox -> CXF -> Klavox ferait dater chaque mot de passe de l'import, et la Sante du coffre taierait les fuites
 * anterieures. `id` = instant exact (ms), valeur = le jour lisible par un autre gestionnaire.
 */
const PASSWORD_DATE_FIELD_ID = 'klavox-password-date';

/**
 * Identifiant du champ portant l'echeance de changement du mot de passe (`password-expiry.ts`) : le CXF v1.0 n'en a
 * pas. Sans lui, Klavox -> CXF -> Klavox la perdait et la Sante du coffre ne rappelait plus rien. Valeur = le jour
 * (`AAAA-MM-JJ`, type `date` du CXF), lisible tel quel par un autre gestionnaire.
 */
const PASSWORD_EXPIRY_FIELD_ID = 'klavox-password-expiry';

/**
 * Identifiant du `custom-fields` portant une cle SSH que le credential `ssh-key` ne sait pas porter (cle chiffree
 * par une phrase de passe, cle OpenSSH RSA/ECDSA, cle publique seule) : relue en paire, jamais perdue.
 */
const SSH_KEY_FIELD_ID = 'klavox-ssh-key';

/**
 * Identifiant du `custom-fields` portant un compte bancaire : le CXF v1.0 n'a pas de credential « compte bancaire ». Un
 * autre gestionnaire recoit des champs libelles (IBAN et numero masques) ; Klavox, a la relecture, refait le compte.
 */
const BANK_ACCOUNT_FIELD_ID = 'klavox-bank-account';

/**
 * Identifiant du `custom-fields` portant un portefeuille crypto : pas de credential « portefeuille » en CXF v1.0 non
 * plus. Phrase de recuperation et phrase de passe BIP39 partent masquees ; le mot de passe du portefeuille voyage dans
 * le `basic-auth`, comme celui de toute entree.
 */
const WALLET_FIELD_ID = 'klavox-crypto-wallet';

/**
 * Identifiant du `custom-fields` portant une base de donnees (moteur, serveur, port, base…) : pas de credential « base de
 * donnees » en CXF v1.0. L'identifiant et le mot de passe de connexion voyagent dans le `basic-auth`, comme ceux de
 * toute entree ; un autre gestionnaire recoit des champs libelles.
 */
const DATABASE_FIELD_ID = 'klavox-database';

/**
 * Identifiant du `custom-fields` portant une licence logicielle : pas de credential « licence » en CXF v1.0. La cle de
 * licence part en `concealed` (masquee chez le gestionnaire suivant), les dates en `date` ; un autre gestionnaire
 * recoit des champs libelles.
 */
const SOFTWARE_LICENSE_FIELD_ID = 'klavox-software-license';

/**
 * Identifiant du `custom-fields` portant un serveur (adresse, console d'administration, hebergeur) : pas de credential
 * « serveur » en CXF v1.0. L'identifiant et le mot de passe du compte voyagent dans le `basic-auth`, comme ceux de toute
 * entree ; le mot de passe de la console part en `concealed` (masque chez le gestionnaire suivant).
 */
const SERVER_FIELD_ID = 'klavox-server';

/**
 * Identifiant du `custom-fields` portant un compte de messagerie (serveurs entrant et sortant, fournisseur) : pas de
 * credential « messagerie » en CXF v1.0. L'identifiant et le mot de passe du compte voyagent dans le `basic-auth` ; le
 * mot de passe SMTP propre part en `concealed`.
 */
const EMAIL_FIELD_ID = 'klavox-email';

/**
 * Identifiant du `custom-fields` portant une carte de membre ou de fidelite : pas de credential « adhesion » en CXF
 * v1.0. Le code PIN part en `concealed` ; une date au jour en `date` (`AAAA-MM-JJ`), au mois en `year-month`
 * (`AAAA-MM`), les deux formes de la spec.
 */
const MEMBERSHIP_FIELD_ID = 'klavox-membership';

/**
 * Identifiant du `custom-fields` portant un dossier medical : pas de credential « sante » en CXF v1.0. Motif,
 * medicament, posologie et remarques partent en `concealed` (le gestionnaire suivant ne les affiche pas en clair), la
 * date de consultation en `date`.
 */
const MEDICAL_FIELD_ID = 'klavox-medical';

/**
 * Identifiant du `custom-fields` portant ce que le credential `api-key` du CXF v1.0 n'a pas (le fichier associe) : la
 * cle, son type, son adresse et ses dates voyagent dans le credential natif.
 */
const API_KEY_FIELD_ID = 'klavox-api-key';

/**
 * Elements lies (`entry-links.ts`) : le CXF v1.0 n'a pas de reference d'element a element. Un `custom-fields` Klavox
 * porte un champ par lien -- `id` = identifiant CXF de l'element designe, valeur = son titre, pour qu'un autre
 * gestionnaire affiche un nom et non un identifiant. Sans lui, Klavox -> CXF -> Klavox perdait tous les liens.
 */
const LINKS_FIELD_ID = 'klavox-links';

// --- Types CXF (sous-ensemble emis et relu) ---------------------------------

export type CxfFieldType = 'string' | 'concealed' | 'email' | 'number' | 'boolean' | 'date' | 'year-month' | 'wifi-network-security-type';

export interface CxfEditableField {
  readonly id: string;
  readonly fieldType: CxfFieldType;
  readonly value: string;
  readonly label?: string;
}

/** Credential CXF : `type` discrimine, le reste depend du type (spec CXF §Credential). */
export interface CxfCredential {
  readonly type: string;
  readonly [key: string]: unknown;
}

export interface CxfItem {
  readonly id: string;
  readonly creationAt: number;
  readonly modifiedAt: number;
  readonly type: 'login' | 'document' | 'identity';
  readonly title: string;
  readonly subtitle?: string;
  readonly credentials: readonly CxfCredential[];
  readonly tags?: readonly string[];
}

export interface CxfCollection {
  readonly id: string;
  readonly creationAt: number;
  readonly modifiedAt: number;
  readonly title: string;
  readonly items: readonly { readonly item: string }[];
  readonly subCollections?: readonly CxfCollection[];
}

export interface CxfAccount {
  readonly id: string;
  readonly userName: string;
  readonly email: string;
  readonly collections: readonly CxfCollection[];
  readonly items: readonly CxfItem[];
}

/** Charge CXF (`Header` de la spec) : ce que CXP transporterait sous HPKE. */
export interface CxfHeader {
  readonly version: number;
  readonly exporter: string;
  /** Secondes UNIX (spec CXF), pas des millisecondes. */
  readonly timestamp: number;
  readonly accounts: readonly CxfAccount[];
}

/** Fichier reellement ecrit sur disque : la charge CXF n'y figure que scellee. */
export interface CxfExportFile {
  readonly format: typeof CXF_EXPORT_FORMAT;
  readonly cxfVersion: number;
  readonly kdf: KdfParams;
  /** Sel Argon2id de CE fichier (32 octets, CSPRNG). Non secret. */
  readonly saltBase64: string;
  readonly payload: SealedString;
}

export class CxfParseError extends Error {
  override readonly name = 'CxfParseError';
  constructor(message: string) {
    super(`export CXF illisible : ${message}`);
  }
}

// --- Lecture defensive ------------------------------------------------------

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type JsonObject = { [key: string]: Json };

function asObject(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as JsonObject) : null;
}

function asArray(value: unknown): Json[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Valeur d'un champ CXF. Selon le credential et l'emetteur, un champ est une
 * chaine nue ou un `EditableField` — les deux sont acceptes plutot que de
 * perdre la valeur sur un detail de serialisation.
 */
function fieldValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  // Un champ `boolean` ecrit en booleen JSON plutot qu'en texte (« true ») : l'etat ne se perd pas.
  if (typeof value === 'boolean') return String(value);
  const object = asObject(value);
  if (object === null) return '';
  return typeof object['value'] === 'string' ? object['value'] : '';
}

function fieldLabel(value: unknown, fallback: string): string {
  const object = asObject(value);
  const label = object === null ? '' : typeof object['label'] === 'string' ? object['label'] : '';
  return label.length > 0 ? label : fallback;
}

// --- Construction de la charge CXF (Klavox -> CXF) --------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function editable(id: string, value: string, fieldType: CxfFieldType, label: string): CxfEditableField {
  return { id, fieldType, value, label };
}

/**
 * Identifiant CXF : la spec impose du base64url. Les identifiants Klavox sont
 * des UUID — on les encode tels quels, ce qui reste reversible et ne cree
 * aucune collision.
 */
function cxfId(id: string): string {
  return bytesToBase64Url(utf8ToBytes(id));
}

function customFieldsCredential(id: string, label: string, fields: readonly CxfEditableField[]): CxfCredential | null {
  return fields.length === 0 ? null : { type: 'custom-fields', id, label, fields };
}

/** Champs personnalises de l'entree + historique et date des mots de passe, sans perte. */
function extraCredentials(secrets: EntrySecrets, createdAt: number): CxfCredential[] {
  const credentials: CxfCredential[] = [];
  const custom: CxfEditableField[] = [];
  for (const field of secrets.customFields ?? []) {
    const value = text(field?.value);
    if (value.length === 0) continue;
    // Case a cocher : `boolean` natif du CXF (valeur `true` / `false`), relu comme tel par Bitwarden et Klavox.
    const fieldType: CxfFieldType = field?.kind === 'boolean' ? 'boolean' : field?.hidden === true ? 'concealed' : 'string';
    custom.push(editable(text(field?.name) || 'champ', value, fieldType, text(field?.name) || 'Champ'));
  }
  const customCredential = customFieldsCredential('klavox-custom-fields', 'Champs personnalises', custom);
  if (customCredential !== null) credentials.push(customCredential);

  // Le CXF n'a pas de case « ancien mot de passe » : un champ personnalise
  // identifie la porte pour que Klavox -> CXF -> Klavox ne perde rien, et que
  // les autres gestionnaires l'affichent au lieu de le jeter.
  const history = readPasswordHistory(secrets).map(({ password, changedAt }) =>
    editable(String(changedAt), password, 'concealed', new Date(changedAt).toISOString().slice(0, 10)),
  );
  const historyCredential = customFieldsCredential(PASSWORD_HISTORY_FIELD_ID, 'Anciens mots de passe', history);
  if (historyCredential !== null) credentials.push(historyCredential);

  if (text(secrets.password).length > 0) {
    const changedAt = lastPasswordChange(secrets, createdAt);
    const day = new Date(changedAt).toISOString().slice(0, 10);
    credentials.push(customFieldsCredential(PASSWORD_DATE_FIELD_ID, 'Date du mot de passe', [editable(String(changedAt), day, 'date', 'Mot de passe pose le')])!);
    const expiry = readPasswordExpiry(secrets);
    // La regle de renouvellement voyage avec l'echeance qu'elle arme, dans le meme champ Klavox.
    const renewDays = readPasswordRenewDays(secrets);
    const renewal = renewDays === null ? [] : [editable('renewDays', String(renewDays), 'number', 'Renouveler tous les (jours)')];
    if (expiry !== null) credentials.push(customFieldsCredential(PASSWORD_EXPIRY_FIELD_ID, 'Echeance du mot de passe', [editable('expiry', expiry, 'date', 'A changer avant le'), ...renewal])!);
  }
  return credentials;
}

/** Champs types (carte/identite) -> `EditableField`, en conservant la cle Klavox comme `id`. */
function typedCredential(secrets: EntrySecrets, group: TypedFieldGroup): CxfCredential | null {
  const fields = readTypedFields(secrets, group);
  const CONCEALED = new Set(['number', 'cvv', 'pin', 'idCardNumber', 'passportNumber', 'licenseNumber', 'socialSecurityNumber', 'taxNumber']);
  if (group === 'card') {
    const expiry = normalizeExpiry(fields['expiry'] ?? '');
    const number = fields['number'] ?? '';
    // `credit-card` est un type CXF natif : une carte exportee vers un autre
    // gestionnaire y arrive comme une carte, pas comme une note.
    return {
      type: 'credit-card',
      number,
      fullName: fields['cardholder'] ?? '',
      cardType: detectCardBrand(number),
      verificationNumber: fields['cvv'] ?? '',
      pin: fields['pin'] ?? '',
      // Spec CXF : `expiryDate` en `YYYY-MM`.
      expiryDate: expiry === null ? '' : `${expiry.slice(3)}-${expiry.slice(0, 2)}`,
    };
  }
  // L'identite part en `custom-fields` : les types d'identite du CXF
  // (`person-name`, `address`, `passport`…) eclateraient une seule fiche
  // Klavox en cinq credentials, et la relecture ne serait plus fidele. Les
  // cles Klavox servent d'`id`, le libelle reste lisible par un humain.
  const list: CxfEditableField[] = [];
  for (const [key, value] of Object.entries(fields)) {
    list.push(editable(key, value, CONCEALED.has(key) ? 'concealed' : key === 'email' ? 'email' : 'string', key));
  }
  return customFieldsCredential('klavox-identity', 'Identite', list);
}

/**
 * Cle SSH de l'entree. Credential `ssh-key` natif (PKCS#8 en base64url, spec CXF) des que la cle s'y prete : un autre
 * gestionnaire la recoit comme une cle SSH. Sinon `custom-fields` masque : la cle voyage quand meme, et revient en paire.
 */
function sshKeyCredential(secrets: EntrySecrets): CxfCredential | null {
  const material = readSshKey(secrets);
  if (material.privateKey.length === 0 && material.publicKey.length === 0) return null;
  const pkcs8 = sshKeyToPkcs8(material);
  if (pkcs8 !== null) {
    const privateKey = toCxfBase64Url(pkcs8.der);
    pkcs8.der.fill(0);
    return { type: 'ssh-key', keyType: pkcs8.keyType, privateKey, ...(pkcs8.comment.length > 0 ? { keyComment: pkcs8.comment } : {}) };
  }
  const fields = [
    ...(material.privateKey.length > 0 ? [editable('privateKey', material.privateKey, 'concealed', 'Cle privee')] : []),
    ...(material.publicKey.length > 0 ? [editable('publicKey', material.publicKey, 'string', 'Cle publique')] : []),
  ];
  return customFieldsCredential(SSH_KEY_FIELD_ID, 'Cle SSH', fields);
}

/**
 * Reseau Wi-Fi : credential `wifi` natif du CXF v1.0 (`ssid`, `networkSecurityType`, `passphrase`, `hidden`). Le mot de
 * passe de l'entree EST la cle du reseau : il part en `passphrase`, jamais en `basic-auth` (un autre gestionnaire en
 * ferait un identifiant de site).
 */
function wifiCredential(secrets: EntrySecrets): CxfCredential | null {
  const fields = readTypedFields(secrets, 'wifi');
  const passphrase = text(secrets.password);
  const security = wifiSecurity(fields['security'] ?? '');
  if (fields['ssid'] === undefined && passphrase.length === 0 && security === null) return null;
  return {
    type: 'wifi',
    ...(fields['ssid'] !== undefined ? { ssid: editable('ssid', fields['ssid'], 'string', 'Nom du reseau') } : {}),
    ...(security !== null ? { networkSecurityType: editable('networkSecurityType', security, 'wifi-network-security-type', 'Securite') } : {}),
    ...(passphrase.length > 0 ? { passphrase: editable('passphrase', passphrase, 'concealed', 'Mot de passe du reseau') } : {}),
    hidden: editable('hidden', fields['hidden'] === 'true' ? 'true' : 'false', 'boolean', 'Reseau masque'),
  };
}

/**
 * Cle d'API : credential `api-key` natif du CXF v1.0 (`key`, `username`, `keyType`, `url`, `validFrom`, `expiryDate`). La
 * cle de l'entree part masquee (`concealed`) ; le fichier associe, que la spec ne prevoit pas, en `custom-fields` Klavox.
 */
function apiKeyCredentials(secrets: EntrySecrets): CxfCredential[] {
  const fields = readTypedFields(secrets, 'api-key');
  const key = text(secrets.password);
  const username = text(secrets.username);
  const date = (value: string | undefined): string | null => (value === undefined ? null : normalizeDocumentDate(value));
  const validFrom = date(fields['apiValidFrom']);
  const expiry = date(fields['apiExpiry']);
  const credentials: CxfCredential[] = [
    {
      type: 'api-key',
      ...(key.length > 0 ? { key: editable('key', key, 'concealed', 'Cle') } : {}),
      ...(username.length > 0 ? { username: editable('username', username, 'string', 'Identifiant') } : {}),
      ...(fields['apiKeyType'] !== undefined ? { keyType: editable('keyType', fields['apiKeyType'], 'string', 'Type de cle') } : {}),
      ...(fields['apiEndpoint'] !== undefined ? { url: editable('url', fields['apiEndpoint'], 'string', 'Adresse de l’API') } : {}),
      // Spec CXF : `date` en `YYYY-MM-DD`, la forme que garde deja l'entree.
      ...(validFrom !== null ? { validFrom: editable('validFrom', validFrom, 'date', 'Valable a partir du') } : {}),
      ...(expiry !== null ? { expiryDate: editable('expiryDate', expiry, 'date', 'Expire le') } : {}),
    },
  ];
  const extra = API_KEY_FIELDS.filter((spec) => spec.key === 'apiFileName' && fields[spec.key] !== undefined).map((spec) =>
    editable(spec.key, fields[spec.key]!, 'string', spec.label),
  );
  const custom = customFieldsCredential(API_KEY_FIELD_ID, 'Cle d’API', extra);
  if (custom !== null) credentials.push(custom);
  return credentials;
}

function itemCredentials(item: VaultItem): CxfCredential[] {
  const secrets = item.secrets;
  const credentials: CxfCredential[] = [];
  // Reseau Wi-Fi : son mot de passe part dans le credential `wifi` (`wifiCredential`) ; cle d'API : identifiant et cle
  // partent dans le credential `api-key` (`apiKeyCredential`) -- un `basic-auth` en ferait un identifiant de site.
  const username = item.type === 'api-key' ? '' : text(secrets.username);
  const password = item.type === 'wifi' || item.type === 'api-key' ? '' : text(secrets.password);
  const urls = (secrets.uris ?? []).map((uri) => text(uri)).filter((uri) => uri.length > 0);

  if (item.type === 'login' || username.length > 0 || password.length > 0 || urls.length > 0) {
    credentials.push({
      type: 'basic-auth',
      urls,
      ...(username.length > 0 ? { username: editable('username', username, 'string', 'Identifiant') } : {}),
      ...(password.length > 0 ? { password: editable('password', password, 'concealed', 'Mot de passe') } : {}),
    });
  }

  const totpSecret = text(secrets.totpSecret);
  if (totpSecret.length > 0) credentials.push(cxfTotpCredential(totpSecret, text(secrets.username), text(secrets.name)));

  // `passkey` est un type CXF natif : une passkey confiee a Klavox repart chez
  // le gestionnaire suivant comme une passkey utilisable, pas comme une note.
  for (const passkey of readPasskeys(secrets)) {
    credentials.push({
      type: 'passkey',
      credentialId: passkey.credentialId,
      rpId: passkey.rpId,
      userName: passkey.userName,
      userDisplayName: passkey.userDisplayName,
      userHandle: passkey.userHandle,
      key: passkey.privateKey,
      createdAt: Math.floor(passkey.createdAt / 1000),
      ...(passkey.fido2Extensions !== undefined ? { fido2Extensions: passkey.fido2Extensions } : {}),
    });
  }

  const sshKey = sshKeyCredential(secrets);
  if (sshKey !== null) credentials.push(sshKey);

  const notes = text(secrets.notes);
  if (notes.length > 0) credentials.push({ type: 'note', content: editable('note', notes, 'string', 'Notes') });

  if (item.type === 'card' || item.type === 'identity') {
    const typed = typedCredential(secrets, item.type);
    if (typed !== null) credentials.push(typed);
  }
  if (item.type === 'wifi') {
    const wifi = wifiCredential(secrets);
    if (wifi !== null) credentials.push(wifi);
  }
  if (item.type === 'bank') {
    const bank = readTypedFields(secrets, 'bank');
    const fields = BANK_FIELDS.filter((spec) => bank[spec.key] !== undefined).map((spec) =>
      editable(spec.key, bank[spec.key]!, spec.sensitive === true ? 'concealed' : 'string', spec.label),
    );
    const credential = customFieldsCredential(BANK_ACCOUNT_FIELD_ID, 'Compte bancaire', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'wallet') {
    const wallet = readTypedFields(secrets, 'wallet');
    const fields = WALLET_FIELDS.filter((spec) => wallet[spec.key] !== undefined).map((spec) =>
      editable(spec.key, wallet[spec.key]!, spec.sensitive ? 'concealed' : 'string', spec.label),
    );
    const credential = customFieldsCredential(WALLET_FIELD_ID, 'Portefeuille crypto', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'api-key') credentials.push(...apiKeyCredentials(secrets));
  if (item.type === 'database') {
    const database = readTypedFields(secrets, 'database');
    const fields = DATABASE_FIELDS.filter((spec) => database[spec.key] !== undefined).map((spec) => editable(spec.key, database[spec.key]!, 'string', spec.label));
    const credential = customFieldsCredential(DATABASE_FIELD_ID, 'Base de donnees', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'software-license') {
    const license = readTypedFields(secrets, 'software-license');
    const fields = SOFTWARE_LICENSE_FIELDS.filter((spec) => license[spec.key] !== undefined).map((spec) =>
      editable(spec.key, license[spec.key]!, spec.sensitive === true ? 'concealed' : isSoftwareLicenseDateKey(spec.key) ? 'date' : 'string', spec.label),
    );
    const credential = customFieldsCredential(SOFTWARE_LICENSE_FIELD_ID, 'Licence logicielle', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'server') {
    const server = readTypedFields(secrets, 'server');
    const fields = SERVER_FIELDS.filter((spec) => server[spec.key] !== undefined).map((spec) =>
      editable(spec.key, server[spec.key]!, spec.sensitive === true ? 'concealed' : 'string', spec.label),
    );
    const credential = customFieldsCredential(SERVER_FIELD_ID, 'Serveur', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'email') {
    const email = readTypedFields(secrets, 'email');
    const fields = EMAIL_FIELDS.filter((spec) => email[spec.key] !== undefined).map((spec) =>
      editable(spec.key, email[spec.key]!, spec.sensitive === true ? 'concealed' : 'string', spec.label),
    );
    const credential = customFieldsCredential(EMAIL_FIELD_ID, 'Compte de messagerie', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'membership') {
    const membership = readTypedFields(secrets, 'membership');
    const fields = MEMBERSHIP_FIELDS.filter((spec) => membership[spec.key] !== undefined).map((spec) => {
      const value = membership[spec.key]!;
      if (spec.sensitive === true) return editable(spec.key, value, 'concealed', spec.label);
      if (!isMembershipDateKey(spec.key)) return editable(spec.key, value, 'string', spec.label);
      // `MM/AAAA` -> `AAAA-MM` ; une date illisible (import ancien) part telle quelle, en texte.
      const month = /^(\d{2})\/(\d{4})$/u.exec(value);
      if (month !== null) return editable(spec.key, `${month[2]}-${month[1]}`, 'year-month', spec.label);
      return editable(spec.key, value, normalizeDocumentDate(value) === value ? 'date' : 'string', spec.label);
    });
    const credential = customFieldsCredential(MEMBERSHIP_FIELD_ID, 'Carte de membre', fields);
    if (credential !== null) credentials.push(credential);
  }
  if (item.type === 'medical') {
    const medical = readTypedFields(secrets, 'medical');
    const fields = MEDICAL_FIELDS.filter((spec) => medical[spec.key] !== undefined).map((spec) => {
      const value = medical[spec.key]!;
      if (spec.sensitive === true) return editable(spec.key, value, 'concealed', spec.label);
      // Une date illisible (import ancien) part telle quelle, en texte.
      if (spec.key === 'recordDate') return editable(spec.key, value, normalizeDocumentDate(value) === value ? 'date' : 'string', spec.label);
      return editable(spec.key, value, 'string', spec.label);
    });
    const credential = customFieldsCredential(MEDICAL_FIELD_ID, 'Dossier medical', fields);
    if (credential !== null) credentials.push(credential);
  }

  credentials.push(...extraCredentials(secrets, item.createdAt));
  return credentials;
}

const CXF_ITEM_TYPES: Record<VaultItemType, CxfItem['type']> = {
  login: 'login',
  note: 'document',
  card: 'document',
  identity: 'identity',
  // Le CXF v1.0 n'a pas de type d'element « cle SSH » : l'element est un
  // document, la cle voyage dans son credential `ssh-key`.
  'ssh-key': 'document',
  // Pas de type d'element « reseau » non plus : la configuration voyage dans le credential `wifi`.
  wifi: 'document',
  // Ni type d'element ni credential « compte bancaire » : document + `custom-fields` Klavox.
  bank: 'document',
  wallet: 'document',
  database: 'document',
  // Ni type d'element « cle d'API » : la cle voyage dans son credential `api-key`.
  'api-key': 'document',
  'software-license': 'document',
  server: 'document',
  email: 'document',
  membership: 'document',
  medical: 'document',
};

/** Collections CXF = arborescence de dossiers Klavox, hierarchie comprise. */
function buildCollections(folders: readonly VaultFolder[], items: readonly VaultItem[]): CxfCollection[] {
  const itemsByFolder = new Map<string, string[]>();
  for (const item of items) {
    if (item.folderId === null) continue;
    const list = itemsByFolder.get(item.folderId) ?? [];
    list.push(cxfId(item.id));
    itemsByFolder.set(item.folderId, list);
  }
  const childrenOf = new Map<string | null, VaultFolder[]>();
  for (const folder of folders) {
    const list = childrenOf.get(folder.parentId) ?? [];
    list.push(folder);
    childrenOf.set(folder.parentId, list);
  }
  // Garde-fou : un parent circulaire (donnee corrompue ou hostile) ne doit pas
  // faire boucler l'export — meme borne de profondeur que l'arbre de l'UI.
  const build = (parentId: string | null, depth: number): CxfCollection[] => {
    if (depth > 16) return [];
    return (childrenOf.get(parentId) ?? []).map((folder) => {
      const subCollections = build(folder.id, depth + 1);
      return {
        id: cxfId(folder.id),
        creationAt: Math.floor(folder.createdAt / 1000),
        modifiedAt: Math.floor(folder.updatedAt / 1000),
        title: text(folder.secrets.name) || 'Dossier',
        items: (itemsByFolder.get(folder.id) ?? []).map((item) => ({ item })),
        ...(subCollections.length > 0 ? { subCollections } : {}),
      };
    });
  };
  return build(null, 0);
}

export interface BuildCxfInput {
  /** E-mail du compte : `userName`/`email` de l'unique compte CXF exporte. */
  readonly email: string;
  /** Entrees DECHIFFREES, tombstones exclus par l'appelant. */
  readonly items: readonly VaultItem[];
  readonly folders: readonly VaultFolder[];
  readonly now?: number;
}

/** Liens de l'entree vers les elements exportes avec elle ; un lien vers un element hors de l'export n'a rien a designer. */
function linksCredential(item: VaultItem, exported: ReadonlyMap<string, VaultItem>): CxfCredential[] {
  const fields = readLinks(item.secrets).flatMap((id) => {
    const target = exported.get(id);
    return target === undefined || id === item.id ? [] : [editable(cxfId(id), text(target.secrets.name) || 'Sans nom', 'string', 'Element lie')];
  });
  const credential = customFieldsCredential(LINKS_FIELD_ID, 'Elements lies', fields);
  return credential === null ? [] : [credential];
}

function itemTags(item: VaultItem): { tags?: string[] } {
  const tags = [
    ...(item.favorite ? [CXF_TAG_FAVORITE] : []),
    ...(isArchived(item.secrets) ? [CXF_TAG_ARCHIVED] : []),
    ...(isRepromptProtected(item.secrets) ? [CXF_TAG_REPROMPT] : []),
    ...(isHealthExcluded(item.secrets) ? [CXF_TAG_HEALTH_EXCLUDED] : []),
    ...readIgnoredHealthAlerts(item.secrets).map((alert) => CXF_TAG_HEALTH_IGNORED[alert]),
    ...readTags(item.secrets).filter((tag) => !isStateTag(tag)),
  ];
  return tags.length > 0 ? { tags } : {};
}

/** Construit la charge CXF v1.0 d'un coffre deja deverrouille. */
/**
 * Credential `totp` du CXF depuis la graine du coffre. Le CXF porte une graine
 * base32 et ses parametres a part : une URI `otpauth://` (graine a parametres
 * non standard) ou `steam://` ecrite telle quelle dans `secret` sous
 * `digits: 6` faisait calculer un code faux au gestionnaire suivant. Steam :
 * `algorithm: 'steam'` sur 5 caracteres, la forme de Bitwarden
 * (`OTPHashAlgorithm::Unknown("steam")`). Graine illisible : partie telle
 * quelle aux parametres par defaut, comme avant -- rien n'est perdu.
 */
export function cxfTotpCredential(totpSecret: string, username: string, issuer: string): CxfCredential {
  let seed: TotpSeed;
  try {
    seed = parseTotpSeed(totpSecret);
  } catch {
    return { type: 'totp', secret: totpSecret, period: 30, digits: 6, algorithm: 'sha1', username, issuer };
  }
  const steam = seed.encoder === 'steam';
  return {
    type: 'totp',
    secret: seed.secret.replace(/[\s-]+/gu, '').replace(/=+$/u, '').toUpperCase(),
    period: seed.periodSeconds,
    digits: steam ? STEAM_CODE_LENGTH : seed.digits,
    algorithm: steam ? 'steam' : seed.algorithm,
    username,
    issuer,
  };
}

/**
 * Graine du coffre depuis un credential `totp` du CXF, ou `null` (elle part
 * alors en notes) si ses parametres ne produiraient pas un code juste. Une
 * graine base32 a parametres non standard (8 chiffres, 60 s, SHA-256) devient
 * une URI `otpauth://` -- celle que `parseTotpSeed` relit -- au lieu d'etre
 * releguee en notes sans code ; `algorithm: 'steam'` devient `steam://`. Une
 * URI deja portee par `secret` (anciens exports de Klavox) garde ses propres
 * parametres.
 */
function totpSeedFromCxf(credential: Record<string, unknown>, secret: string): string | null {
  if (/^(otpauth|steam):\/\//iu.test(secret)) return secret;
  const period = credential['period'] === undefined ? TOTP_DEFAULTS.periodSeconds : Number(credential['period']);
  const digits = credential['digits'] === undefined ? TOTP_DEFAULTS.digits : Number(credential['digits']);
  const algorithm = typeof credential['algorithm'] === 'string' ? credential['algorithm'].toLowerCase() : TOTP_DEFAULTS.algorithm;
  let seed: string;
  if (algorithm === 'steam') {
    // Steam ne connait que 30 s ; `digits` est implicite (5 caracteres).
    if (period !== TOTP_DEFAULTS.periodSeconds) return null;
    seed = `steam://${secret}`;
  } else if (period === TOTP_DEFAULTS.periodSeconds && digits === TOTP_DEFAULTS.digits && algorithm === TOTP_DEFAULTS.algorithm) {
    return secret;
  } else {
    const issuer = fieldValue(credential['issuer']);
    const account = fieldValue(credential['username']);
    const label = issuer.length > 0 && account.length > 0 ? `${issuer}:${account}` : issuer || account || 'TOTP';
    const params = new URLSearchParams({ secret, algorithm: algorithm.toUpperCase(), digits: String(digits), period: String(period) });
    if (issuer.length > 0) params.set('issuer', issuer);
    seed = `otpauth://totp/${encodeURIComponent(label)}?${params.toString()}`;
  }
  try {
    parseTotpSeed(seed);
  } catch {
    return null;
  }
  return seed;
}

export function buildCxfHeader(input: BuildCxfInput): CxfHeader {
  const now = input.now ?? Date.now();
  const live = input.items.filter((item) => item.deletedAt === null);
  const exported = new Map(live.map((item) => [item.id, item]));
  const items: CxfItem[] = live
    .map((item) => ({
      id: cxfId(item.id),
      creationAt: Math.floor(item.createdAt / 1000),
      modifiedAt: Math.floor(item.updatedAt / 1000),
      type: CXF_ITEM_TYPES[item.type],
      title: text(item.secrets.name) || 'Sans nom',
      credentials: [...itemCredentials(item), ...linksCredential(item, exported)],
      ...itemTags(item),
    }));
  return {
    version: CXF_VERSION,
    exporter: CXF_EXPORTER,
    timestamp: Math.floor(now / 1000),
    accounts: [
      {
        id: cxfId(input.email),
        userName: input.email,
        email: input.email,
        collections: buildCollections(input.folders, input.items.filter((item) => item.deletedAt === null)),
        items,
      },
    ],
  };
}

// --- Enveloppe chiffree -----------------------------------------------------

/**
 * `fileKey = Argon2id(mot de passe d'export, sel aleatoire)` — memes
 * parametres que le compte (RFC 9106 §4, plancher `CLAUDE.md`), verifies avant
 * usage pour qu'un fichier ne puisse pas imposer un KDF affaibli.
 */
async function deriveCxfFileKey(password: string, salt: Uint8Array, params: KdfParams, onProgress?: (fraction: number) => void): Promise<SecretKey> {
  assertKdfParams(params);
  const bytes = await argon2idAsync(utf8ToBytes(password.normalize('NFC')), salt, {
    t: params.iterations,
    m: params.memoryKiB,
    p: params.parallelism,
    dkLen: SYMMETRIC_KEY_LENGTH,
    onProgress,
  });
  return SecretKey.adopt(bytes, 'symmetricKey', SYMMETRIC_KEY_LENGTH);
}

/**
 * En-tete canonique lie en donnees associees : sel et parametres Argon2id sont
 * en clair dans le fichier, mais les alterer (downgrade du KDF, substitution
 * de sel) invalide le tag au lieu de passer inapercu.
 */
function cxfAssociatedData(file: Omit<CxfExportFile, 'payload'>): string {
  return JSON.stringify([
    file.format,
    file.cxfVersion,
    file.kdf.algorithm,
    file.kdf.memoryKiB,
    file.kdf.iterations,
    file.kdf.parallelism,
    file.saltBase64,
  ]);
}

/** Erreur levee quand le mot de passe d'export est trop court pour ce qu'il protege. */
export class WeakCxfPasswordError extends Error {
  override readonly name = 'WeakCxfPasswordError';
  constructor() {
    super(`le mot de passe de l'export doit faire au moins ${MIN_CXF_PASSWORD_LENGTH} caracteres`);
  }
}

/**
 * Scelle une charge CXF sous un mot de passe d'export.
 *
 * @throws {WeakCxfPasswordError} mot de passe sous le plancher
 */
export async function encryptCxfExport(
  header: CxfHeader,
  password: string,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<CxfExportFile> {
  if (typeof password !== 'string' || password.length < MIN_CXF_PASSWORD_LENGTH) throw new WeakCxfPasswordError();
  const salt = randomBytes(CXF_SALT_LENGTH);
  const envelope = { format: CXF_EXPORT_FORMAT, cxfVersion: CXF_VERSION, kdf: params, saltBase64: bytesToBase64(salt) } as const;
  const fileKey = await deriveCxfFileKey(password, salt, params);
  try {
    const payload = sealToString(fileKey, utf8ToBytes(JSON.stringify(header)), {
      context: CXF_SEAL_CONTEXT,
      associatedData: cxfAssociatedData(envelope),
    });
    return { ...envelope, payload };
  } finally {
    fileKey.destroy();
  }
}

export function serializeCxfExport(file: CxfExportFile): string {
  return JSON.stringify(file, null, 2);
}

/** Un fichier d'export CXF chiffre par Klavox se reconnait a son `format`. */
export function isCxfExportFile(data: unknown): data is CxfExportFile {
  return asObject(data)?.['format'] === CXF_EXPORT_FORMAT;
}

/**
 * Une charge CXF nue (produite par un autre gestionnaire) se reconnait a son
 * `exporter` + `accounts`. Le couple evite de confondre avec un `.1pux`, qui
 * porte aussi un tableau `accounts`.
 */
export function isCxfPayload(data: unknown): data is CxfHeader {
  const object = asObject(data);
  return object !== null && typeof object['exporter'] === 'string' && Array.isArray(object['accounts']);
}

/**
 * Ouvre un export CXF chiffre.
 *
 * @throws {CxfParseError} enveloppe structurellement invalide
 * @throws {ImportPasswordInvalidError} mot de passe incorrect ou fichier altere
 */
export async function decryptCxfExport(file: CxfExportFile, password: string, onProgress?: (fraction: number) => void): Promise<CxfHeader> {
  const kdf = file?.kdf;
  if (typeof file?.saltBase64 !== 'string' || typeof file?.payload !== 'string' || kdf === undefined) {
    throw new CxfParseError('enveloppe incomplete');
  }
  const salt = base64ToBytes(file.saltBase64);
  if (salt.length !== CXF_SALT_LENGTH) throw new CxfParseError('sel de longueur inattendue');
  let fileKey: SecretKey;
  try {
    fileKey = await deriveCxfFileKey(password, salt, kdf, onProgress);
  } catch (cause) {
    // Parametres Argon2id hors politique : le fichier est refuse, jamais lu
    // avec un KDF affaibli.
    throw new CxfParseError(cause instanceof Error ? cause.message : 'parametres KDF refuses');
  }
  try {
    const plaintext = openFromString(fileKey, file.payload, {
      context: CXF_SEAL_CONTEXT,
      associatedData: cxfAssociatedData({
        format: CXF_EXPORT_FORMAT,
        cxfVersion: typeof file.cxfVersion === 'number' ? file.cxfVersion : CXF_VERSION,
        kdf,
        saltBase64: file.saltBase64,
      }),
    });
    const header: unknown = JSON.parse(bytesToUtf8(plaintext));
    if (!isCxfPayload(header)) throw new CxfParseError('charge dechiffree non conforme (aucun compte)');
    return header;
  } catch (cause) {
    if (cause instanceof CxfParseError) throw cause;
    if (cause instanceof DecryptionFailedError) throw new ImportPasswordInvalidError(CXF_FORMAT_LABEL);
    throw new CxfParseError(cause instanceof Error ? cause.message : 'enveloppe invalide');
  } finally {
    fileKey.destroy();
  }
}

// --- Lecture d'une charge CXF (CXF -> Klavox) -------------------------------

/** Champs d'identite CXF (`id` de champ, ou cle d'un type natif) -> champ Klavox. */
const CXF_IDENTITY_KEYS: Record<string, string> = {
  title: 'title',
  given: 'firstName',
  givenInformal: 'firstName',
  firstName: 'firstName',
  family: 'lastName',
  lastName: 'lastName',
  birthDate: 'birthDate',
  email: 'email',
  phone: 'phone',
  phoneNumber: 'phone',
  company: 'company',
  streetAddress: 'address',
  address: 'address',
  postalCode: 'postalCode',
  city: 'city',
  territory: 'region',
  region: 'region',
  country: 'country',
  idCardNumber: 'idCardNumber',
  passportNumber: 'passportNumber',
  licenseNumber: 'licenseNumber',
  idCardExpiry: 'idCardExpiry',
  passportExpiry: 'passportExpiry',
  licenseExpiry: 'licenseExpiry',
  socialSecurityNumber: 'socialSecurityNumber',
  taxNumber: 'taxNumber',
};

/** Types de credentials sans equivalent Klavox : recopies en notes, jamais perdus en silence. */
const CXF_NOTE_FALLBACK_FIELDS = ['privateKey', 'publicKey', 'keyComment', 'keyType', 'ssid', 'networkSecurityType', 'key', 'validFrom', 'fileName'];

interface CxfItemDraft {
  username: string;
  password: string;
  uris: string[];
  notes: string[];
  totpSecret: string;
  typed: Record<string, string>;
  group: TypedFieldGroup | null;
  history: PasswordHistoryEntry[];
  /** Pose du mot de passe courant relue du champ Klavox ; `null` si l'export ne la porte pas. */
  passwordChangedAt: number | null;
  /** Echeance relue du champ Klavox, brute : `importedEntryToSecrets` la normalise ou l'ecarte. */
  passwordExpiry: string | null;
  /** Regle de renouvellement relue du champ Klavox, brute : `importedEntryToSecrets` la borne ou l'ecarte. */
  passwordRenewDays: string | null;
  passkeys: PasskeyCredential[];
  customFields: EntryCustomField[];
  /** Cle SSH relue d'un `custom-fields` Klavox (deja en paire). */
  sshKey: SshKeyMaterial | null;
  /** Cle SSH d'un credential `ssh-key` : PKCS#8, a remettre en paire (`completeSshKeys`). */
  sshPkcs8: { readonly der: Uint8Array; readonly comment: string } | null;
  /** `passphrase` d'un credential `wifi` : le mot de passe de l'entree si elle devient un reseau Wi-Fi. */
  wifiPassphrase: string;
  /** `key` d'un credential `api-key` : le mot de passe de l'entree si elle devient une cle d'API. */
  apiKey: string;
  /** Identifiants CXF des elements lies, relus du champ Klavox ; resolus apres creation (`resolveImportedLinks`). */
  linkedSourceIds: string[];
}

function applyCredential(credential: JsonObject, draft: CxfItemDraft): void {
  const type = typeof credential['type'] === 'string' ? credential['type'] : '';
  switch (type) {
    case 'basic-auth': {
      const username = fieldValue(credential['username']);
      const password = fieldValue(credential['password']);
      if (username.length > 0 && draft.username.length === 0) draft.username = username;
      if (password.length > 0 && draft.password.length === 0) draft.password = password;
      for (const url of asArray(credential['urls'])) {
        const value = fieldValue(url);
        if (value.length > 0) draft.uris.push(value);
      }
      return;
    }
    case 'generated-password': {
      const password = fieldValue(credential['password']) || fieldValue(credential['value']);
      if (password.length > 0 && draft.password.length === 0) draft.password = password;
      return;
    }
    case 'passkey': {
      const credentialId = fieldValue(credential['credentialId']);
      // Emetteurs constates : `key` (spec CXF) ou `privateKey` selon la version.
      const privateKey = fieldValue(credential['key']) || fieldValue(credential['privateKey']);
      const rpId = fieldValue(credential['rpId']);
      if (credentialId.length === 0 || privateKey.length === 0) {
        // Sans cle privee, ce n'est plus une passkey : la ranger comme telle
        // laisserait croire que le compte est recuperable. On le DIT.
        draft.notes.push(`Passkey incomplete (sans cle privee) : ${rpId || credentialId || 'origine inconnue'}`);
        return;
      }
      const createdAt = Number(credential['createdAt']);
      const extensions = asObject(credential['fido2Extensions']);
      draft.passkeys.push({
        credentialId,
        rpId,
        // Spec CXF : `userName` ; certains emetteurs ecrivent `username`.
        userName: fieldValue(credential['userName']) || fieldValue(credential['username']),
        userDisplayName: fieldValue(credential['userDisplayName']),
        userHandle: fieldValue(credential['userHandle']),
        privateKey,
        // Le CXF horodate en secondes, le coffre en millisecondes.
        createdAt: Number.isSafeInteger(createdAt) && createdAt > 0 ? createdAt * 1000 : 0,
        ...(extensions !== null ? { fido2Extensions: extensions } : {}),
      });
      return;
    }
    case 'totp': {
      const raw = fieldValue(credential['secret']) || fieldValue(credential['uri']);
      const normalized = normalizeTotpSecret(raw);
      const seed = normalized.secret.length > 0 ? totpSeedFromCxf(credential, normalized.secret) : null;
      if (seed !== null && draft.totpSecret.length === 0) draft.totpSecret = seed;
      else if (raw.length > 0) draft.notes.push(normalized.extraNote || `TOTP : ${raw}`);
      return;
    }
    case 'ssh-key': {
      const der = decodeCxfPrivateKey(fieldValue(credential['privateKey']));
      if (der === null || draft.sshKey !== null || draft.sshPkcs8 !== null) break;
      draft.sshPkcs8 = { der, comment: fieldValue(credential['keyComment']) };
      // Dates et origine de la cle : sans case Klavox, gardees en notes.
      for (const [key, label] of [
        ['creationDate', 'Cle SSH creee le'],
        ['expiryDate', 'Cle SSH expire le'],
        ['keyGenerationSource', 'Cle SSH generee par'],
      ] as const) {
        const value = fieldValue(credential[key]);
        if (value.length > 0) draft.notes.push(`${label} : ${value}`);
      }
      return;
    }
    case 'wifi': {
      if (draft.group !== null) {
        // Element deja carte, identite ou reseau : ce reseau-ci ne l'ecrase pas. Son nom part en notes, sa cle dans un
        // champ masque -- jamais perdue, jamais affichee en clair.
        const otherSsid = fieldValue(credential['ssid']);
        const otherKey = fieldValue(credential['passphrase']);
        if (otherSsid.length > 0) draft.notes.push(`Reseau Wi-Fi : ${otherSsid}`);
        if (otherKey.length > 0) draft.customFields.push({ name: `Mot de passe Wi-Fi${otherSsid.length > 0 ? ` (${otherSsid})` : ''}`, value: otherKey, hidden: true });
        return;
      }
      draft.group = 'wifi';
      const ssid = fieldValue(credential['ssid']);
      if (ssid.length > 0) draft.typed['ssid'] = ssid;
      const rawSecurity = fieldValue(credential['networkSecurityType']);
      const security = wifiSecurity(rawSecurity);
      if (security !== null) draft.typed['security'] = security;
      // Valeur d'une version future de la spec : dite en notes, jamais jetee.
      else if (rawSecurity.length > 0) draft.notes.push(`Securite Wi-Fi : ${rawSecurity}`);
      if (fieldValue(credential['hidden']).toLowerCase() === 'true') draft.typed['hidden'] = 'true';
      draft.wifiPassphrase = fieldValue(credential['passphrase']);
      return;
    }
    case 'api-key': {
      const key = fieldValue(credential['key']);
      // `custom-fields` Klavox de la cle (fichier associe) deja lu : meme cle, pas une autre.
      if ((draft.group !== null && draft.group !== 'api-key') || draft.apiKey.length > 0) {
        // Element deja carte, reseau ou autre cle : cette cle-ci ne l'ecrase pas. Elle part dans un champ masque --
        // jamais perdue, jamais affichee en clair dans les notes.
        if (key.length > 0) draft.customFields.push({ name: 'Cle d’API', value: key, hidden: true });
        return;
      }
      draft.group = 'api-key';
      draft.apiKey = key;
      const username = fieldValue(credential['username']);
      if (username.length > 0 && draft.username.length === 0) draft.username = username;
      for (const [cxfKey, klavoxKey] of [
        ['keyType', 'apiKeyType'],
        ['url', 'apiEndpoint'],
        ['validFrom', 'apiValidFrom'],
        ['expiryDate', 'apiExpiry'],
      ] as const) {
        const value = fieldValue(credential[cxfKey]);
        if (value.length === 0) continue;
        // Date illisible : dite en notes plutot que d'empecher l'enregistrement de la cle (`apiKeyProblem`).
        if ((klavoxKey === 'apiValidFrom' || klavoxKey === 'apiExpiry') && normalizeDocumentDate(value) === null) draft.notes.push(`${cxfKey} : ${value}`);
        else draft.typed[klavoxKey] ??= value;
      }
      return;
    }
    case 'note': {
      const content = fieldValue(credential['content']);
      if (content.length > 0) draft.notes.push(content);
      return;
    }
    case 'credit-card': {
      draft.group = 'card';
      draft.typed['number'] = fieldValue(credential['number']);
      draft.typed['cardholder'] = fieldValue(credential['fullName']);
      draft.typed['cvv'] = fieldValue(credential['verificationNumber']);
      draft.typed['pin'] = fieldValue(credential['pin']);
      draft.typed['bank'] = fieldValue(credential['cardType']);
      draft.typed['expiry'] = normalizeExpiry(fieldValue(credential['expiryDate'])) ?? '';
      return;
    }
    case 'person-name':
    case 'address':
    case 'identity-document':
    case 'passport':
    case 'drivers-license': {
      draft.group = draft.group ?? 'identity';
      for (const [key, value] of Object.entries(credential)) {
        if (key === 'type' || key === 'id' || key === 'label') continue;
        const mapped =
          CXF_IDENTITY_KEYS[key] ?? (key === 'number' ? documentNumberKey(type) : key === 'expiryDate' ? documentExpiryKey(type) : undefined);
        const content = key === 'expiryDate' ? (normalizeDocumentDate(fieldValue(value)) ?? fieldValue(value)) : fieldValue(value);
        if (content.length === 0) continue;
        if (mapped === undefined) draft.notes.push(`${key} : ${content}`);
        else draft.typed[mapped] ??= content;
      }
      return;
    }
    case 'custom-fields': {
      const id = typeof credential['id'] === 'string' ? credential['id'] : '';
      const label = typeof credential['label'] === 'string' ? credential['label'] : 'Champs';
      for (const rawField of asArray(credential['fields'])) {
        const field = asObject(rawField);
        if (field === null) continue;
        const value = fieldValue(field['value']);
        if (value.length === 0) continue;
        const fieldId = typeof field['id'] === 'string' ? field['id'] : '';
        if (id === SSH_KEY_FIELD_ID && (fieldId === 'privateKey' || fieldId === 'publicKey')) {
          draft.sshKey = { privateKey: '', publicKey: '', ...draft.sshKey, [fieldId]: value };
          continue;
        }
        if (id === PASSWORD_HISTORY_FIELD_ID) {
          const changedAt = Number(fieldId);
          draft.history.push({ password: value, changedAt: Number.isSafeInteger(changedAt) && changedAt >= 0 ? changedAt : 0 });
          continue;
        }
        if (id === PASSWORD_DATE_FIELD_ID) {
          const changedAt = Number(fieldId);
          if (Number.isSafeInteger(changedAt) && changedAt > 0) draft.passwordChangedAt = changedAt;
          continue;
        }
        if (id === LINKS_FIELD_ID) {
          if (fieldId.length > 0 && !draft.linkedSourceIds.includes(fieldId)) draft.linkedSourceIds.push(fieldId);
          continue;
        }
        if (id === PASSWORD_EXPIRY_FIELD_ID) {
          if (fieldId === 'renewDays') draft.passwordRenewDays ??= value;
          else draft.passwordExpiry ??= value;
          continue;
        }
        if (id === BANK_ACCOUNT_FIELD_ID && BANK_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'bank';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === API_KEY_FIELD_ID && API_KEY_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = draft.group ?? 'api-key';
          if (draft.group === 'api-key') {
            draft.typed[fieldId] ??= value;
            continue;
          }
        }
        if (id === DATABASE_FIELD_ID && DATABASE_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'database';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === SERVER_FIELD_ID && SERVER_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'server';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === EMAIL_FIELD_ID && EMAIL_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'email';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === MEMBERSHIP_FIELD_ID && MEMBERSHIP_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'membership';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === MEDICAL_FIELD_ID && MEDICAL_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'medical';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === SOFTWARE_LICENSE_FIELD_ID && SOFTWARE_LICENSE_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'software-license';
          draft.typed[fieldId] ??= value;
          continue;
        }
        if (id === WALLET_FIELD_ID && WALLET_FIELDS.some((spec) => spec.key === fieldId)) {
          draft.group = 'wallet';
          draft.typed[fieldId] ??= value;
          continue;
        }
        const mapped = CXF_IDENTITY_KEYS[fieldId];
        if (mapped !== undefined) {
          draft.group = draft.group ?? 'identity';
          draft.typed[mapped] ??= value;
          continue;
        }
        // `concealed` (spec CXF) = valeur masquee a l'ecran, comme a l'origine ; `boolean` = case a cocher.
        draft.customFields.push(
          field['fieldType'] === 'boolean'
            ? { name: fieldLabel(field, fieldId || label), value: checkboxValue(value), kind: 'boolean' }
            : { name: fieldLabel(field, fieldId || label), value, hidden: field['fieldType'] === 'concealed' },
        );
      }
      return;
    }
    default:
      break;
  }
  // Cle SSH illisible ou en double, wifi, fichier, type inconnu
  // d'une version future : tout ce qui porte une valeur part en notes.
  for (const key of CXF_NOTE_FALLBACK_FIELDS) {
    const value = fieldValue(credential[key]);
    if (value.length > 0) draft.notes.push(`${key} : ${value}`);
  }
}

/** `expiryDate` d'un document d'identite (`YYYY-MM-DD` selon la spec) : la case Klavox depend du type de document. */
function documentExpiryKey(type: string): string | undefined {
  if (type === 'passport') return 'passportExpiry';
  if (type === 'drivers-license') return 'licenseExpiry';
  if (type === 'identity-document') return 'idCardExpiry';
  return undefined;
}

/** `number` d'un document d'identite : la case Klavox depend du type de document. */
function documentNumberKey(type: string): string {
  if (type === 'passport') return 'passportNumber';
  if (type === 'drivers-license') return 'licenseNumber';
  return 'idCardNumber';
}

/** Chemin de dossier de chaque entree, depuis l'arbre de collections du compte. */
function collectionPaths(collections: readonly Json[], parents: readonly string[], index: Map<string, string[]>, depth = 0): void {
  if (depth > 16) return;
  for (const raw of collections) {
    const collection = asObject(raw);
    if (collection === null) continue;
    const title = typeof collection['title'] === 'string' ? collection['title'].trim() : '';
    const path = title.length > 0 ? [...parents, title] : [...parents];
    for (const rawLink of asArray(collection['items'])) {
      const link = asObject(rawLink);
      const itemId = link === null ? fieldValue(rawLink) : fieldValue(link['item']);
      if (itemId.length > 0 && path.length > 0) index.set(itemId, path);
    }
    collectionPaths(asArray(collection['subCollections']), path, index, depth + 1);
  }
}

function draftToEntry(item: JsonObject, draft: CxfItemDraft, folderPath: readonly string[] | undefined): ImportedEntry {
  const declared = typeof item['type'] === 'string' ? item['type'] : '';
  // PKCS#8 que WebCrypto n'a pas remis en paire (lecture synchrone, navigateur sans Ed25519) : garde tel quel, `ssh` le lit.
  const sshKey = draft.sshKey ?? (draft.sshPkcs8 !== null ? { privateKey: pkcs8Pem(draft.sshPkcs8.der), publicKey: '' } : null);
  const type: VaultItemType =
    draft.group === 'card' || draft.group === 'wifi' || draft.group === 'bank' || draft.group === 'wallet' || draft.group === 'database' || draft.group === 'api-key' ||
    draft.group === 'software-license' || draft.group === 'server' || draft.group === 'email' || draft.group === 'membership' || draft.group === 'medical'
      ? draft.group
      : draft.group === 'identity' || declared === 'identity'
        ? 'identity'
        : draft.password.length > 0 || draft.username.length > 0 || draft.uris.length > 0 || draft.passkeys.length > 0
          ? 'login'
          : sshKey !== null
            ? 'ssh-key'
            : 'note';
  // Cle SSH dans un element qui est aussi un identifiant ou une carte : la fiche n'afficherait pas la paire, elle part
  // en notes (lisible, jamais perdue) plutot que dans des secrets invisibles.
  if (sshKey !== null && type !== 'ssh-key') draft.notes.push(['Cle SSH :', sshKey.privateKey.trim(), sshKey.publicKey].filter((line) => line.length > 0).join('\n'));
  const tags = asArray(item['tags']).map((tag) => fieldValue(tag));
  const userTags = normalizeTags(tags.filter((tag) => !isStateTag(tag)));
  const healthIgnored = IGNORABLE_HEALTH_ALERTS.filter((alert) => tags.includes(CXF_TAG_HEALTH_IGNORED[alert]));
  const title = typeof item['title'] === 'string' ? item['title'].trim() : '';
  const group = typedFieldGroup(type);
  // Reseau Wi-Fi : la cle du reseau devient le mot de passe de l'entree ; un mot de passe `basic-auth` different
  // (interface de la borne, par exemple) reste dans un champ masque plutot que de remplacer la cle.
  let password = draft.password;
  if (type === 'wifi' && draft.wifiPassphrase.length > 0) {
    if (password.length > 0 && password !== draft.wifiPassphrase) draft.customFields.push({ name: 'Mot de passe', value: password, hidden: true });
    password = draft.wifiPassphrase;
  }
  // Cle d'API : la cle devient le mot de passe de l'entree (historique, Sante du coffre) ; un mot de passe `basic-auth`
  // different reste dans un champ masque.
  if (type === 'api-key' && draft.apiKey.length > 0) {
    if (password.length > 0 && password !== draft.apiKey) draft.customFields.push({ name: 'Mot de passe', value: password, hidden: true });
    password = draft.apiKey;
  }
  // Pose du mot de passe : le champ Klavox, sinon la derniere modification de l'element (borne haute : un autre
  // gestionnaire ne date pas le mot de passe, il n'a pu etre pose qu'avant ou a cet instant).
  const modifiedAt = typeof item['modifiedAt'] === 'number' && Number.isSafeInteger(item['modifiedAt']) && item['modifiedAt'] > 0 ? item['modifiedAt'] * 1000 : null;
  const passwordChangedAt = draft.passwordChangedAt ?? modifiedAt;
  return {
    type,
    name: title || draft.typed['ssid'] || draft.uris[0] || draft.username || 'Sans nom',
    username: draft.username,
    password,
    uris: draft.uris,
    notes: joinNotes([typeof item['subtitle'] === 'string' ? item['subtitle'] : '', ...draft.notes]),
    totpSecret: draft.totpSecret,
    favorite: tags.includes(CXF_TAG_FAVORITE),
    ...(tags.includes(CXF_TAG_ARCHIVED) ? { archived: true } : {}),
    ...(tags.includes(CXF_TAG_REPROMPT) ? { reprompt: true } : {}),
    ...(tags.includes(CXF_TAG_HEALTH_EXCLUDED) ? { healthExcluded: true } : {}),
    ...(healthIgnored.length > 0 ? { healthIgnored } : {}),
    ...(userTags.length > 0 ? { tags: userTags } : {}),
    ...(folderPath !== undefined && folderPath.length > 0 ? { folderPath } : {}),
    ...(group !== null ? { fields: normalizeTypedFields(group, draft.typed) } : {}),
    ...(draft.history.length > 0 ? { passwordHistory: draft.history } : {}),
    ...(password.length > 0 && passwordChangedAt !== null ? { passwordChangedAt } : {}),
    ...(password.length > 0 && draft.passwordExpiry !== null ? { passwordExpiry: draft.passwordExpiry } : {}),
    ...(password.length > 0 && draft.passwordRenewDays !== null ? { passwordRenewDays: draft.passwordRenewDays } : {}),
    ...(draft.passkeys.length > 0 ? { passkeys: draft.passkeys } : {}),
    ...(draft.customFields.length > 0 ? { customFields: draft.customFields } : {}),
    ...(sshKey !== null && type === 'ssh-key' ? { sshKey } : {}),
    // Identifiant CXF de l'element : designe par les liens des autres, jamais scelle (`importedEntryToSecrets`).
    ...(typeof item['id'] === 'string' && item['id'].length > 0 ? { sourceId: item['id'] } : {}),
    ...(draft.linkedSourceIds.length > 0 ? { linkedSourceIds: draft.linkedSourceIds } : {}),
  };
}

/**
 * Transforme une charge CXF en entrees importables. Les dossiers (collections)
 * sont conserves, hierarchie comprise ; aucun credential n'est jete en
 * silence. Lecture synchrone : une cle SSH `ssh-key` y reste en PKCS#8, sans
 * cle publique -- l'import de l'IHM passe par `parseCxfImport`, qui la remet en
 * paire.
 *
 * @throws {CxfParseError} charge sans compte exploitable
 */
export function parseCxfHeader(header: unknown): ImportedEntry[] {
  return readCxfDrafts(header).map(({ item, draft, folderPath }) => draftToEntry(item, draft, folderPath));
}

interface CxfDraftedItem {
  readonly item: JsonObject;
  readonly draft: CxfItemDraft;
  readonly folderPath: readonly string[] | undefined;
}

function readCxfDrafts(header: unknown): CxfDraftedItem[] {
  const object = asObject(header);
  const accounts = object === null ? [] : asArray(object['accounts']);
  if (accounts.length === 0) throw new CxfParseError('aucun compte');
  const drafted: CxfDraftedItem[] = [];
  for (const rawAccount of accounts) {
    const account = asObject(rawAccount);
    if (account === null) continue;
    const paths = new Map<string, string[]>();
    collectionPaths(asArray(account['collections']), [], paths);
    for (const rawItem of asArray(account['items'])) {
      const item = asObject(rawItem);
      if (item === null) continue;
      const draft: CxfItemDraft = {
        username: '',
        password: '',
        uris: [],
        notes: [],
        totpSecret: '',
        typed: {},
        group: null,
        history: [],
        passwordChangedAt: null,
        passwordExpiry: null,
        passwordRenewDays: null,
        passkeys: [],
        customFields: [],
        sshKey: null,
        sshPkcs8: null,
        wifiPassphrase: '',
        apiKey: '',
        linkedSourceIds: [],
      };
      for (const rawCredential of asArray(item['credentials'])) {
        const credential = asObject(rawCredential);
        if (credential !== null) applyCredential(credential, draft);
      }
      const id = typeof item['id'] === 'string' ? item['id'] : '';
      drafted.push({ item, draft, folderPath: paths.get(id) });
    }
  }
  return drafted;
}

/**
 * Remet en paire les cles SSH recues en PKCS#8 : cle publique (empreinte de la fiche), et Ed25519 en `openssh-key-v1`
 * pour que l'agent `klavox ssh agent` la serve. Le PKCS#8 est efface une fois converti.
 */
async function completeSshKeys(drafted: readonly CxfDraftedItem[], subtle: SubtleCrypto | undefined): Promise<void> {
  for (const { draft } of drafted) {
    if (draft.sshPkcs8 === null) continue;
    const material = await sshKeyFromPkcs8(draft.sshPkcs8.der, draft.sshPkcs8.comment, subtle);
    if (material === null) continue;
    draft.sshPkcs8.der.fill(0);
    draft.sshPkcs8 = null;
    draft.sshKey = material;
  }
}

/** Lecture complete d'une charge CXF, cles SSH remises en paire par WebCrypto (chemin de l'import de l'IHM). */
async function readCxfEntries(header: unknown, subtle: SubtleCrypto | undefined): Promise<ImportedEntry[]> {
  const drafted = readCxfDrafts(header);
  await completeSshKeys(drafted, subtle);
  return drafted.map(({ item, draft, folderPath }) => draftToEntry(item, draft, folderPath));
}

/**
 * Point d'entree d'import CXF : fichier chiffre Klavox (mot de passe requis)
 * ou charge CXF nue d'un autre gestionnaire.
 *
 * @throws {ImportPasswordRequiredError} export chiffre sans mot de passe fourni
 */
export async function parseCxfImport(
  data: unknown,
  password?: string,
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
  onProgress?: (fraction: number) => void,
): Promise<ImportedEntry[]> {
  if (isCxfExportFile(data)) {
    if (password === undefined || password.length === 0) throw new ImportPasswordRequiredError(CXF_FORMAT_LABEL);
    return readCxfEntries(await decryptCxfExport(data, password, onProgress), subtle);
  }
  return readCxfEntries(data, subtle);
}
